import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PlusAuthenticationService } from '@karve.it/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { EMPTY, Observable, combineLatest, filter, switchMap, take, distinctUntilChanged, skip, startWith, throttleTime } from 'rxjs';
import { JobDetailsV2Fragment, UpdateEventNavigatorGQL } from 'src/generated/graphql.generated';
import { SubSink } from 'subsink';
import { transform } from 'sucrase';

import { GraphQLModule } from '../../core/auth/graphql.module';
import { generateMutationContext } from '../../core/auth/session';
import { OnlineStatusService } from '../../online-status.service';
import { quickJsActions } from '../../quickjs/quickjs.actions';
import { QuickJsService } from '../../quickjs/quickjs.service';
import { deriveNavigatorResume, isNavigatorStarted } from '../job-helpers';
import { JobToolActions } from '../job-tool.actions';
import { selectJob, selectNavigatorUrlForEvent } from '../job-tool.reducer';

import { NavigatorNodeComponent } from './components/navigator-node.component';
import { NavigatorOutboxActions } from './navigator-outbox/navigator-outbox.actions';
import { navigatorOutboxFeature } from './navigator-outbox/navigator-outbox.reducer';

/**
 * Whether the navigator is closed for this event (#99), and the status to name if so. The Start /
 * Continue buttons on the job overview and the workorders tab follow the same rule; this guards the
 * direct URL. A stopped run stays as it is — nothing is written, so ops' reset and step dialog still
 * see it.
 *
 * Cancelled is always closed. Completed is closed only when no run is under way (#99b): creating the
 * invoice at Collect Payment marks the event completed (`invoices.create`), so a crew that reloads on
 * step 16 or 17 must still be let back in to finish.
 */
function navigatorClosedStatus(event: {
    status?: string | null;
    navigatorCurrentStep?: string | null;
    navigatorData?: string | null;
}): string | null {
    if (event.status === 'cancelled') { return 'cancelled'; }
    if (event.status !== 'completed') { return null; }
    const runInProgress = isNavigatorStarted(event) && event.navigatorCurrentStep !== 'completed';
    return runInProgress ? null : 'completed';
}

/**
 * Travel-time fallbacks, in seconds, used when an event has no leg of that type.
 *
 * They are coarse on purpose: they only have to keep the step timer showing *something* plausible
 * when routing data is absent, and a visibly round number is easier to recognise as a default than
 * a precise-looking one. The middle leg is longer because it is the loaded haul between the two
 * homes; the dock legs are a typical yard-to-first-stop run.
 */
const FALLBACK_DOCK_TO_START_SEC = 600;
const FALLBACK_START_TO_END_SEC = 1200;
const FALLBACK_END_TO_DOCK_SEC = 600;

/** Saves dropped together on one replay get one message (#86b). */
const DROPPED_SAVE_MESSAGE_MS = 10_000;


@Component({
    selector: 'app-job-navigator-page',
    standalone: true,
    imports: [CommonModule, NavigatorNodeComponent],
    styles: [`
        /*
         * Page shell — full screen on every touch device, centred card only for mouse users.
         *
         * Phone  (< 600px):            fills the viewport edge-to-edge
         * Touch  (≥ 600px):            still full screen (phone in landscape, tablet); the content
         *                              keeps a readable max width, but there is no grey margin or
         *                              shadow — it should look like an app, not a page (#101)
         * Mouse  (≥ 600px, fine hover): max-width 520px, centred card, background visible
         *
         * The card is gated on the input device, not the width: a landscape phone is 900px+ wide
         * and used to get the card, which reads as a desktop page shrunk onto a phone.
         */
        :host {
            display: flex; flex-direction: column;
            width: 100%; height: 100%;
            background: #e8ecf0;
            align-items: center;
            overflow: hidden;
        }

        /* Phone: fills entire width */
        .nav-page {
            width: 100%; max-width: 100%;
            height: 100%;
            display: flex; flex-direction: column;
            overflow: hidden;
            position: relative;
            background: #f5f7fa;
        }

        /* Wide touch screen: full bleed, page colour behind the content, readable width */
        @media (min-width: 600px) {
            :host { background: #f5f7fa; }
            .nav-page { max-width: 720px; }
        }

        /* Mouse users on a wide screen: give it a card appearance */
        @media (hover: hover) and (pointer: fine) and (min-width: 600px) {
            :host { background: #e8ecf0; justify-content: center; padding: 1.5rem 0; }
            .nav-page {
                max-width: 520px;
                height: calc(100% - 0px);
                max-height: 900px;
                border-radius: 16px;
                box-shadow: 0 8px 40px rgba(0,0,0,0.18);
                overflow: hidden;
            }
        }

        .nav-page > app-navigator-node {
            flex: 1 1 0;
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }

        /* Status screens */
        .status {
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            height: 100%; gap: 1rem; padding: 2rem; text-align: center;
            background: white;
        }
        .status i { font-size: 2rem; }
        .status.error { color: #c00; }
        .status.error i { color: #c00; }
        .status.loading { color: #555; }
        .status.loading i { color: #2d6a2d; }
        .status-text { font-size: 1rem; color: #666; }
        /* Same outlined 44px button as the navigator's other secondary actions (#100b). */
        .back-btn {
            min-height: 44px; padding: 0 1.1rem;
            display: inline-flex; align-items: center; gap: 0.4rem;
            background: white; border: 1.5px solid #2d6a2d; border-radius: 8px;
            color: #2d6a2d; font-size: 0.95rem; font-weight: 600;
            font-family: inherit; cursor: pointer;
        }
        .back-btn i { font-size: 0.95rem; color: inherit; }
        .back-btn:active { background: #e6f1e6; }

        /* ── Offline indicator ── */
        .offline-bar {
            background: #b91c1c; color: white;
            font-size: 0.78rem; font-weight: 600;
            text-align: center; padding: 0.3rem 0.5rem;
            z-index: 10; letter-spacing: 0.03em;
            flex-shrink: 0;
        }
        .offline-bar i { margin-right: 0.35rem; }

        /* Saves waiting in the navigator outbox (#86a): shown online too, while they are sent. */
        .waiting-bar {
            background: #fef3c7; color: #78350f;
            font-size: 0.78rem; font-weight: 600;
            text-align: center; padding: 0.3rem 0.5rem;
            flex-shrink: 0;
        }
        .waiting-bar i { margin-right: 0.35rem; }
    `],
    template: `
        <div class="nav-page">

            <!-- Offline banner — in-flow so it doesn't overlap content -->
            <div class="offline-bar" *ngIf="isOffline">
                <i class="pi pi-wifi"></i>No signal — your progress is saved on this phone and will be sent when you're back online.
            </div>
            <!-- Hidden at zero: a count of 0 is falsy. -->
            <div class="waiting-bar" *ngIf="savesWaiting$ | async as savesWaiting" aria-live="polite">
                <i class="pi pi-cloud-upload"></i>{{ savesWaiting }} {{ savesWaiting === 1 ? 'save' : 'saves' }} waiting
            </div>

            <div class="status loading" *ngIf="loading">
                <i class="pi pi-spin pi-spinner"></i>
                <span class="status-text">Loading navigator...</span>
            </div>
            <div class="status error" *ngIf="error">
                <i class="pi pi-exclamation-circle"></i>
                <span>{{ error }}</span>
                <button type="button" class="back-btn" (click)="backToJob()">
                    <i class="pi pi-arrow-left"></i>Back to job
                </button>
            </div>
            <div class="status" *ngIf="closedStatus">
                <i class="pi pi-lock"></i>
                <span>This move is {{ closedStatus }} — the navigator is closed.</span>
                <button type="button" class="back-btn" (click)="backToJob()">
                    <i class="pi pi-arrow-left"></i>Back to job
                </button>
            </div>
            <app-navigator-node *ngIf="navigatorTree" [node]="navigatorTree"></app-navigator-node>
        </div>
    `,
})
export class JobNavigatorPageComponent implements OnInit, OnDestroy {

    loading = true;
    error: string | null = null;
    /** 'cancelled' / 'completed' when the event is closed and the navigator must not run (#99). */
    closedStatus: string | null = null;
    navigatorTree: any = null;
    isOffline = false;
    /** The crew's saves waiting in the navigator outbox (#86a), across every job on this phone. */
    readonly savesWaiting$: Observable<number>;

    private subs = new SubSink();
    private wakeLock: WakeLockSentinel | null = null;
    /** Set in `ngOnDestroy` so a wake lock that resolves afterwards is released, not stranded. */
    private destroyed = false;
    private readonly actions$ = inject(Actions);

    /**
     * Set once the crew moves the step themselves, which closes the one server-sourced resume
     * correction window in `ngOnInit`. Without it, a slow first fetch could land mid-run and pull a
     * crew who has already advanced back to where the server thinks they are (#46 in miniature).
     */
    private crewHasMovedSinceMount = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private store: Store,
        private http: HttpClient,
        private quickJs: QuickJsService,
        private updateEventNavigatorGQL: UpdateEventNavigatorGQL,
        private graphqlModule: GraphQLModule,
        private auth: PlusAuthenticationService,
        private messages: MessageService,
        private onlineStatus: OnlineStatusService,
    ) {
        this.savesWaiting$ = this.store.select(navigatorOutboxFeature.selectCrewSavesWaiting);
    }

    /**
     * The way out of the closed and error screens (#108), which have no header and so no Exit. Goes
     * to the job's overview, keeping the query string (`zone`). Built from the URL rather than the
     * loaded job, because the error screen can show before or without a job.
     */
    backToJob() {
        const path = this.router.url.split('?')[0];
        const navigatorIdx = path.lastIndexOf('/navigator/');
        this.router.navigate(
            [navigatorIdx >= 0 ? `${path.slice(0, navigatorIdx)}/overview` : '/'],
            { queryParamsHandling: 'preserve' },
        );
    }

    /** A step's title from the loaded script, falling back to its id. */
    private stepTitle(stepId: string | null): string {
        const find = (node: any): any => (node?.type === 'Step' && node.props?.id === stepId)
            ? node
            : (node?.children ?? []).map(find).find(Boolean);
        return `"${ find(this.navigatorTree)?.props?.title ?? stepId ?? 'another step' }"`;
    }

    /** Out of a run that ops reset (#86b): to the job's workorders, where Start begins it again. */
    private leaveToWorkorders() {
        const path = this.router.url.split('?')[0];
        const navigatorIdx = path.lastIndexOf('/navigator/');
        this.router.navigate(
            [navigatorIdx >= 0 ? `${path.slice(0, navigatorIdx)}/workorders` : '/'],
            { queryParamsHandling: 'preserve' },
        );
    }

    ngOnInit() {
        // Hard redirect to login if not authenticated — guards can't reliably
        // fire for this route due to the :jobId prefix redirect in the routing config.
        if (!this.auth.user) {
            console.log('[Navigator] unauthenticated, redirecting to login');
            window.location.href = '/auth/login';
            return;
        }

        document.body.classList.add('navigator-open');
        try { (window as any).Tawk_API?.hideWidget?.(); } catch { /* ignore */ }

        // Keep screen on during move — silently ignore if not supported
        if ('wakeLock' in navigator) {
            (navigator as any).wakeLock.request('screen')
                .then((lock: WakeLockSentinel) => {
                    // The request is async, so it can resolve after the crew has already left the
                    // page. Assigning it then would strand a live sentinel on a dead component and
                    // keep the screen awake for the rest of the session — battery drain on a phone
                    // in a driveway that nobody can explain or undo.
                    if (this.destroyed) { lock.release().catch(() => { /* ignore */ }); return; }
                    this.wakeLock = lock;
                })
                .catch(() => { /* not supported or permission denied — ignore */ });
        }

        const eventId = this.route.snapshot.paramMap.get('eventId');

        // Scope the navigator slice to this event before anything can read or write it.
        // `navigatorCurrentStepId` is one value for the whole session, so without this the step
        // left behind by the previously-opened event seeds this run — NavigatorRoot reads the
        // current value the moment it subscribes, and its `filter(!!stepId)` means the correction
        // that arrives afterwards can never undo it (#30).
        //
        // Must stay the first statement: the restore subscription below dispatches the correct
        // step for *this* event, and that has to land after the clear, not before it.
        if (eventId) {
            this.store.dispatch(quickJsActions.navigatorOpenedForEvent({ eventId }));
        }

        // ── Connection status indicator ───────────────────────────────────────
        // The phone's own offline state as well as the live-updates socket (#86a): with no signal
        // the socket can take a while to notice, and the crew should be told straight away.
        this.subs.sink = combineLatest([
            this.onlineStatus.isOnline$,
            this.graphqlModule.connectionStatus.pipe(
                skip(1), // ignore the initial 'disconnected' default — only react to real transitions
                startWith('connected'),
                distinctUntilChanged(),
            ),
        ]).subscribe(([ online, status ]) => {
            this.isOffline = !online || status === 'disconnected';
        });

        // ── Resume the crew's step: at most twice per mount, then never again ────────────────
        //
        // This replaces a subscription that re-derived the step from `selectJob` on *every* job
        // change. That was #46. `selectJob` is shared state written by echoes, refetches, optimistic
        // patches and ops alike, and the old dedupe key included `navigatorData.stepStartTime`, so
        // every inventory patch re-triggered a full re-derivation. A `jobChanged` echo describes what
        // is **persisted**; a crew with a write in flight is legitimately ahead of that — measured
        // 30-80s behind on a throttled run, walking them back two or three steps with a payload that
        // was entirely correct about the database. No guard can fix that, so the derivation is gone.
        //
        // What replaces it:
        //   1. an immediate derive from whatever the store already holds, so the crew is not parked
        //      on step 1 waiting for the network;
        //   2. **one** correction, if the first server-sourced job arrives after that. `jobLoaded`
        //      carries `source: 'cache' | 'server'` (`job.effects.ts:381`), which is exactly what the
        //      old "does NOT use take(1) so it picks up the network response after a stale cache hit"
        //      comment needed and never had.
        //
        // After that, only the crew (`navigatorStepConfirmed` / `navigatorStepUndone`) or an explicit
        // `navigatorStepOverridden` moves the step.
        const resumeFrom = (job: JobDetailsV2Fragment | null | undefined) => {
            const event = job?.events?.find(e => e.id === eventId);
            const { stepId, stepStartTime } = deriveNavigatorResume(event);

            // Nothing confirmed and no persisted start: a genuinely fresh run, leave it alone.
            // Dispatching here would stamp a start time over a run that has not begun.
            if (!stepId && stepStartTime == null) { return; }

            this.store.dispatch(quickJsActions.navigatorStateRestored({
                stepId,
                stepStartTime: stepStartTime ?? Date.now(),
            }));
        };

        // 1. Immediate, one-shot.
        this.subs.sink = this.store.select(selectJob).pipe(
            filter(job => !!job),
            take(1),
        ).subscribe(job => resumeFrom(job));

        // 2. One correction from the first authoritative load, and only if the crew has not already
        //    moved themselves. Without that check a slow first fetch could land mid-run and pull a
        //    crew who has advanced back to where the server thinks they are — the original bug in
        //    miniature. `take(1)` bounds it regardless: a later refetch must never re-position them.
        this.subs.sink = this.actions$.pipe(
            ofType(JobToolActions.jobLoaded),
            filter(({ source }) => source === 'server'),
            filter(() => !this.crewHasMovedSinceMount),
            take(1),
        ).subscribe(({ job }) => resumeFrom(job));

        // ── #52: report any move that came from outside this device ─────────────────────────
        //
        // `MessageService` is provided on `AppComponent` (`app.component.ts:46`), a *component*
        // injector, so only something inside the component tree can reach the instance that
        // `<p-toast>` actually renders. An effect cannot — it lives in an environment injector, and
        // injecting it there threw `NullInjectorError` at route activation and took this whole page
        // down with it.
        //
        // The step id is the crew's own vocabulary (it is what the script names its steps), so it is
        // more use to them than an index.
        this.subs.sink = this.actions$.pipe(
            ofType(quickJsActions.navigatorStepOverridden),
            filter(({ eventId: overriddenEventId }) => overriddenEventId === eventId),
        ).subscribe(({ stepId }) => {
            this.messages.add({
                severity: 'info',
                summary: 'Step changed from outside this device',
                detail: `Moved to "${stepId}" by the office or another device.`,
                life: 8000,
            });
        });

        // ── #86b: a save of the crew's that ops overtook ──────────────────────────────────────
        //
        // One message per burst: every save queued before ops changed the run is dropped the same
        // way, and they arrive together on replay. Moving the crew is the navigator reducer's job.
        this.subs.sink = this.actions$.pipe(
            ofType(NavigatorOutboxActions.saveDropped),
            filter(({ eventId: droppedEventId }) => droppedEventId === eventId),
            throttleTime(DROPPED_SAVE_MESSAGE_MS),
        ).subscribe(({ reason, currentStep }) => {
            if (reason === 'reset') {
                this.messages.add({
                    severity: 'warn',
                    summary: 'Ops reset the navigator',
                    detail: 'Your progress from while you had no signal was not applied. Start the navigator again.',
                    life: 12000,
                });
                this.leaveToWorkorders();
                return;
            }
            this.messages.add({
                severity: 'info',
                summary: `Ops moved you to ${ this.stepTitle(currentStep) }`,
                detail: 'Your step change from while you had no signal was not applied. Your other times were kept.',
                life: 12000,
            });
        });

        // ── #86c: crew inventory changes where the office's version was kept ────────────────────
        //
        // Message only: the crew's marks stay as they are — they may be far past the inventory step
        // by now, and only ops can move them back to it.
        this.subs.sink = this.actions$.pipe(
            ofType(NavigatorOutboxActions.inventoryMerged),
            filter(({ eventId: mergedEventId, kept }) => mergedEventId === eventId && kept.length > 0),
        ).subscribe(({ kept }) => {
            const names = [ ...new Set(kept.map((k) => k.name)) ].join(', ');
            this.messages.add({
                severity: 'warn',
                summary: `The office changed ${ names } while you were offline`,
                detail: 'Their version was kept — talk to ops if something looks wrong.',
                life: 15000,
            });
        });

        // The server refused the crew's inventory save (#86c). Their edits are back on this phone as
        // unsaved edits (`restoreRefusedNavigatorInventory`), and only a Confirm Scope sends them.
        this.subs.sink = this.actions$.pipe(
            ofType(NavigatorOutboxActions.inventorySaveRefused),
            filter(({ eventId: refusedEventId }) => refusedEventId === eventId),
        ).subscribe(() => {
            this.messages.add({
                severity: 'error',
                summary: 'Your inventory changes were not saved',
                detail: 'They are still on this phone. Confirm Scope again to send them; if you are past that step, tell ops.',
                life: 20000,
            });
        });

        // Anything the crew does themselves closes the correction window above.
        this.subs.sink = this.actions$.pipe(
            ofType(quickJsActions.navigatorStepConfirmed, quickJsActions.navigatorStepUndone),
            take(1),
        ).subscribe(() => { this.crewHasMovedSinceMount = true; });

        // ── Load and build the navigator tree (once) ─────────────────────────
        this.subs.sink = this.store.select(selectJob).pipe(
            filter(job => !!job),
            take(1),
            switchMap(job => {
                const event = job.events?.find(e => e.id === eventId);
                if (!event) throw new Error(`Event ${eventId} not found`);

                // Closed events never build the tree, so no step renders and the 'started' write
                // below never runs.
                const closed = navigatorClosedStatus(event);
                if (closed) {
                    this.loading = false;
                    this.closedStatus = closed;
                    return EMPTY;
                }

                return this.store.select(selectNavigatorUrlForEvent(event)).pipe(
                    take(1),
                    switchMap(url => {
                        if (!url) throw new Error(`No navigator URL configured for "${event.type}"`);

                        return this.http.get(url, { responseType: 'text' }).pipe(
                            switchMap(async script => {
                                const cjsCode = transform(script, {
                                    transforms: ['typescript', 'jsx', 'imports'],
                                    jsxPragma: 'h',
                                    jsxFragmentPragma: 'Fragment',
                                }).code;
                                const compiled = `(function() { const exports = {}; ${cjsCode}; Object.assign(globalThis, exports); })();`;

                                await this.quickJs.loadNavigatorScript(compiled);

                                const customerUser = job.users?.find(u => u.role === 'customer')?.user;
                                const startLoc = job.locations?.find(l => l.locationType === 'start')?.location;
                                const endLoc = job.locations?.find(l => l.locationType === 'end')?.location;

                                // ── Pull calculator output from job metadata ──────────
                                const meta: Record<string, any> = (job as any).metadata ?? {};
                                const calcOutput: Record<string, any> = {};
                                for (const [k, v] of Object.entries(meta)) {
                                    const prefix = 'calculatorOutput__';
                                    if (k.startsWith(prefix)) {
                                        calcOutput[k.slice(prefix.length)] = v;
                                    }
                                }

                                // ── Pull dock location ─────────────────────────────
                                const dockLoc = job.locations?.find(
                                    (l: any) => l.locationType === 'dock'
                                )?.location;

                                // ── Pull event locations ───────────────────────────
                                //
                                // Resolved by `type`, not by array position. The old `eventLocs[0..2]`
                                // assumed every event is exactly dock → start → end → dock; an event
                                // missing its dock legs, or carrying an extra stop, then read each
                                // travel time off the wrong leg and fed a wrong budget into every
                                // step timer. Wrong numbers, no error.
                                //
                                // `travelTimeToNextLocation` is time *from* this location to the next,
                                // so each leg is named by where it starts.
                                //
                                // The type vocabulary is the production one, measured rather than
                                // assumed: `basestart` / `start` / `end` / `baseend` account for
                                // ~99.9% of rows, with `2ndStop`, `storage` and `office` in the tail.
                                // There is no `dock` type — the dock legs are `basestart`/`baseend`.
                                const eventLocs = event.locations ?? [];
                                const legFrom = (type: string) =>
                                    eventLocs.find((l: any) => l.type === type)?.travelTimeToNextLocation;

                                const dockToStartTravel = legFrom('basestart') ?? FALLBACK_DOCK_TO_START_SEC;
                                const startToEndTravel  = legFrom('start')     ?? FALLBACK_START_TO_END_SEC;
                                const endToDockTravel   = legFrom('end')       ?? FALLBACK_END_TO_DOCK_SEC;

                                 // ── Attendees (crew) ───────────────────────────────
                                 const attendees = (event.attendees ?? []).map((a: any) => ({
                                     userId: a.user?.id,
                                     name: [a.user?.givenName, a.user?.familyName].filter(Boolean).join(' ') || 'Crew Member',
                                     role: a.role ?? 'crew',
                                     startTime: (a as any).startTime ?? null,
                                     endTime: (a as any).endTime ?? null,
                                     breakMinutes: (a as any).breakMinutes ?? null,
                                 })).filter((a: any) => a.role !== 'customer');

                                // ── Charges / total ────────────────────────────────
                                const charges = event.charges ?? [];
                                const total: number = (event as any).total ?? 0;
                                const currency = (event as any).currency ?? 'CAD';

                                const input = {
                                    // Job / customer
                                    customer: customerUser ? {
                                        name: [customerUser.givenName, customerUser.familyName].filter(Boolean).join(' '),
                                        email: customerUser.email,
                                        phone: customerUser.phone,
                                    } : {},
                                    locations: {
                                        start: startLoc ? {
                                            addressLineOne: startLoc.addressLineOne,
                                            city: startLoc.city,
                                            accessNotes: (startLoc as any).notes ?? (startLoc as any).accessNotes,
                                        } : null,
                                        end: endLoc ? {
                                            addressLineOne: endLoc.addressLineOne,
                                            city: endLoc.city,
                                        } : null,
                                        dock: dockLoc ? {
                                            addressLineOne: dockLoc.addressLineOne,
                                            city: dockLoc.city,
                                        } : null,
                                    },

                                    // Route distances from event locations
                                    dock_start: { estimatedTime: dockToStartTravel, travelTime: dockToStartTravel },
                                    start_end:  { estimatedTime: startToEndTravel,  travelTime: startToEndTravel },
                                    end_dock:   { estimatedTime: endToDockTravel,   travelTime: endToDockTravel },

                                    // Calculator-derived performance expectations
                                    totalWeight:           calcOutput['totalWeight']      ?? calcOutput['weight']    ?? 5000,
                                    crewSize:              calcOutput['crewSize']         ?? calcOutput['numCrew']   ?? 2,
                                    benchmarkRate:         calcOutput['benchmarkRate']    ?? 750,
                                    estimatedLoadSeconds:  calcOutput['estimatedLoadSeconds']   ??
                                                          (calcOutput['loadingHours']
                                                              ? Math.round(calcOutput['loadingHours'] * 3600)
                                                              : 3600),
                                    estimatedUnloadSeconds: calcOutput['estimatedUnloadSeconds'] ??
                                                          (calcOutput['unloadingHours']
                                                              ? Math.round(calcOutput['unloadingHours'] * 3600)
                                                              : 2700),

                                    // Financials
                                    charges,
                                    total,
                                    currency,

                                    // Crew
                                    attendees,
                                    eventId: eventId ?? '',
                                    appointmentId: eventId ?? '',

                                    // Notes — pass plain text for fallback display in NavJobNotesComponent
                                    // The Quill-rich version is read from the NgRx store by the component.
                                    // crewSummary is a Summary scalar: { text?, contents? }
                                    // contents is a JSON-serialised Quill Delta; extract plain text from it.
                                    jobNotes: (() => {
                                        const summary = (job as any).crewSummary;
                                        if (!summary) return (job as any).notes ?? '';
                                        if (summary.text) return summary.text;
                                        try {
                                            const delta = JSON.parse(summary.contents ?? '{}');
                                            return (delta.ops ?? [])
                                                .map((op: any) => (typeof op.insert === 'string' ? op.insert : ''))
                                                .join('');
                                        } catch { return (job as any).notes ?? ''; }
                                    })(),
                                    eventNotes: event.notes ?? '',

                                    // IDs (needed for PandaDocSigner, system comments, etc.)
                                    jobId: job.id,
                                };
                                // Same derivation the resume path uses. This was a hand-rolled
                                // second copy that had already drifted from it; `deriveNavigatorResume`
                                // is now the only implementation.
                                const { stepId: restoredStepId } = deriveNavigatorResume(event);

                                const state = {
                                    atLocation: 'start',
                                    totalWeight: calcOutput['totalWeight'] ?? 5000,
                                    ...(restoredStepId ? { currentStepId: restoredStepId } : {}),
                                };

                const tree = this.quickJs.getNavigatorTree(input, state);
                                return {
                                    tree,
                                    eventId,
                                    url,
                                    restoredStepId,
                                };
                            }),
                        );
                    }),
                );
            }),
        ).subscribe({
            next: ({ tree, eventId: eid, url, restoredStepId: restored }) => {
                this.loading = false;
                this.navigatorTree = tree;


                // Only write 'started' on a genuinely fresh session — if we're
                // restoring a prior step, don't overwrite navigatorCurrentStep.
                if (!restored) {
                    // `generateMutationContext()` is not optional here (#51). Without it this write
                    // carries no `x-session`, so its own echo comes back as `remoteJobUpdated` —
                    // indistinguishable from another machine. It was measured arriving 18s late and
                    // asserting `'started'` over a crew three steps in.
                    this.updateEventNavigatorGQL.mutate({
                        eventId: eid,
                        navigatorUrl: url,
                        currentStep: 'started',
                    }, { context: generateMutationContext() })
                        .subscribe({ error: err => console.error('Failed to persist navigator start:', err) });
                }

            },
            error: err => {
                this.loading = false;
                this.error = err?.message ?? 'Failed to load navigator';
                console.error(err);
            },
        });
    }

    ngOnDestroy() {
        this.destroyed = true;
        // `navigator-chat-open` too: left behind, it would let the chat bubble cover the next run (#110).
        document.body.classList.remove('navigator-open', 'navigator-chat-open');
        try { (window as any).Tawk_API?.showWidget?.(); } catch { /* ignore */ }
        this.subs.unsubscribe();
        this.wakeLock?.release().catch(() => { /* ignore */ });
    }
}
